import {
    IJobParam,
    IJobParamCreateLoadOptions,
    IJobParamManager,
} from "./PA.Job.IF.JobParam";
import {PAdbContext} from "../PA.Core/PAdbContext";
import * as _ from "lodash"
import {
    WB_JobParamRow,
    WB_MasterDataSetRow,
    WB_SalesOrgRow,
    WB_UIAppExecutableRow
} from "../PA.Core/PAdbSchemaRows";
import {
    esWB_Job_Param,
    esWB_MasterData_Set,
    esWB_SalesOrg,
    esWB_UI_AppExecutable
} from "../PA.Core/PAdbSchema";
import {JobParam} from "./JobParam";

export class JobParamManager
    implements IJobParamManager {

    dbContext: PAdbContext

    constructor() {
    }

    private getDbContext(options: { dbContext?: PAdbContext }): PAdbContext {
        if (!_.isEmpty(this.dbContext))
            return this.dbContext

        let dbContext = options.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext()
        this.dbContext = dbContext
        return this.dbContext
    }


    async findByWbId(wbJobParamId: number, options?: IJobParamCreateLoadOptions): Promise<IJobParam> {
        let dbContext = this.getDbContext(options)

        let jobParamRows = <WB_JobParamRow[]>await dbContext.findUsingCache(esWB_Job_Param, [wbJobParamId || -1])
        if (_.isEmpty(jobParamRows))
            return undefined
        let jobParamRow = _.first(jobParamRows)
        await this.syncOptions(jobParamRow, options)
        return await this.createFromExistingRow(jobParamRow, options)
    }

    async findByEnterpriseScope(wbMdSetId: number, wbSalesOrgId: number, sourceType?: string, options?: IJobParamCreateLoadOptions): Promise<IJobParam[]> {
        let retVal: IJobParam[] = []
        let dbContext = this.getDbContext(options)
        let jobParamRows = <WB_JobParamRow[]>await dbContext.findAll(esWB_Job_Param, {
            where: {
                WB_MDSET_ID: wbMdSetId,
                WB_SALESORG_ID: wbSalesOrgId,
                DELETED_IND: {neq: '1'}
            }
        })
        if (_.isEmpty(jobParamRows))
            return undefined

        if (!_.isEmpty(sourceType))
            jobParamRows = _.filter(jobParamRows, aRow => aRow.cif.SOURCE_TYPE == sourceType)

        for (let row of jobParamRows) {
            await this.syncOptions(row, options)
            let jobParam = await this.createFromExistingRow(row, options)
            retVal.push(jobParam)
        }
        return retVal
    }

    async findBySourceType(sourceType: string, sourceTypeWbId: number, options: IJobParamCreateLoadOptions): Promise<IJobParam> {
        let dbContext = this.getDbContext(options)
        let jobParamRows = <WB_JobParamRow[]>await dbContext.findAll(esWB_Job_Param, {
            where: {
                SOURCE_TYPE: sourceType,
                SOURCE_WB_ID: sourceTypeWbId,
                DELETED_IND: {neq: '1'}
            }
        })

        if (_.isEmpty(jobParamRows))
            return undefined

        let jobParamRow = _.first(jobParamRows)
        await this.syncOptions(jobParamRow, options)
        return await this.createFromExistingRow(jobParamRow, options)
    }

    private async createFromExistingRow(pJobParamRow: WB_JobParamRow, options: IJobParamCreateLoadOptions): Promise<IJobParam> {
        let dbContext = this.getDbContext(options)

        // Use the record Resolver yo
        if (_.isEmpty(pJobParamRow))
            return undefined

        let aJP = new JobParam(pJobParamRow)
        await aJP.OnLoadComplete()

        return aJP
    }

    async findOrCreate(options: IJobParamCreateLoadOptions): Promise<IJobParam> {
        let dbContext = this.getDbContext(options)

        let rv: IJobParam = undefined

        // Try to find by id, metas.., else create..
        let wbJobParamId = +options.JobParamWbId
        if (_.isNumber(wbJobParamId)) {
            rv = await this.findByWbId(wbJobParamId, {dbContext: options.dbContext})
            if (!_.isEmpty(rv))
                return rv
        }

        let sourceWbId = +options.sourceWbId
        let sourceType = options.sourceType

        if (_.isNumber(sourceWbId) && !_.isEmpty(sourceType)) {
            rv = await this.findBySourceType(sourceType, sourceWbId, options)
            if (!_.isEmpty(rv))
                return rv
        }
        // Create a Record
        let newJobParam = <WB_JobParamRow>await dbContext.createRecordWithKey(esWB_Job_Param, {})
        await this.syncOptions(newJobParam, options)
        let newJP = new JobParam(newJobParam)
        await newJP.OnLoadComplete()

        return newJP
    }

    private async syncOptions(pJobParamRow: WB_JobParamRow, opts: IJobParamCreateLoadOptions): Promise<void> {
        let dbContext = this.getDbContext(opts)

        if (_.isEmpty(pJobParamRow))
            return

        let jpr = pJobParamRow.cif
        jpr.JOB_PARAM_ID = opts.JobParamId || jpr.JOB_PARAM_ID
        jpr.JOB_PARAM_NAME = opts.JobParamName || jpr.JOB_PARAM_NAME
        jpr.WB_MDSET_ID = opts.wbMasterDataSetId || jpr.WB_MDSET_ID
        jpr.SOURCE_WB_ID = opts.sourceWbId || jpr.SOURCE_WB_ID
        jpr.SOURCE_TYPE = opts.sourceType || jpr.SOURCE_TYPE
        jpr.WB_ID_TARGET = opts.wbTargetId || jpr.WB_ID_TARGET
        jpr.JOB_PROCESSOR_TYPE = opts.JobProcessorType || jpr.JOB_PROCESSOR_TYPE


        // Check the FK for
        // 1. Master Data Set
        // 2. Sales Org
        // 3. App Exec

        //--- 1. Master Data Set
        if (_.isEmpty(pJobParamRow.WB_MasterDataSetRow)) {
            let mdr = <WB_MasterDataSetRow>await dbContext.find(esWB_MasterData_Set, jpr.WB_MDSET_ID)
            if (!_.isEmpty(mdr)) {
                pJobParamRow.cif.WB_MDSET_ID = mdr.cif.WB_MDSET_ID
                pJobParamRow.WB_MasterDataSetRow = mdr
            }
        }

        //--- 2. SalesOrg
        if (_.isEmpty(pJobParamRow.WB_SalesOrgRow)) {
            if (Number(opts?.wbSalesOrgId) > 0) {
                let sor = <WB_SalesOrgRow>await dbContext.find(esWB_SalesOrg, opts.wbSalesOrgId)
                if (!_.isEmpty(sor)) {
                    pJobParamRow.cif.WB_SALESORG_ID = sor.cif.WB_SALESORG_ID
                    pJobParamRow.WB_SalesOrgRow = sor
                }
            } else {
                let sor = <WB_SalesOrgRow>await dbContext.find(esWB_SalesOrg, jpr.WB_SALESORG_ID)
                if (!_.isEmpty(sor))
                    pJobParamRow.WB_SalesOrgRow = sor
            }
        }

        //--- 3. App Exec
        if (_.isEmpty(pJobParamRow.WB_UIAppExecutableRow)) {
            let appExecId = opts?.appExecId || ""
            if (!_.isEmpty(appExecId)) {
                let execRows = <WB_UIAppExecutableRow[]>await dbContext.findAll(esWB_UI_AppExecutable, {
                    where: {
                        APPEXEC_ID: appExecId,
                        DELETED_IND: {neq: '1'}
                    }
                })
                if (execRows.length) {
                    let execRow = _.first(execRows)
                    pJobParamRow.cif.WB_UI_APPEXEC_ID = execRow.cif.WB_UI_APPEXEC_ID
                    pJobParamRow.WB_UIAppExecutableRow = execRow
                }
            } else {
                if (jpr.cif.WB_UI_APPEXEC_ID_TARGET > 0) {
                    let execRow = <WB_UIAppExecutableRow>await dbContext.find(esWB_UI_AppExecutable, jpr.cif.WB_UI_APPEXEC_ID_TARGET)
                    if (!_.isEmpty(execRow))
                        pJobParamRow.WB_UIAppExecutableRow = execRow
                }
            }
        }

    }
}
